import React, { createContext, useCallback, useRef } from 'react';
import {
  setServedLocationsMap,
  setServedLocationsMapReverse,
  setServedLocationsOptions,
  setCountryCodeToNameMap,
} from './actions';
import useLocationsReducer from './use-locations-reducer';
import ZApiClient from '../../../../../utils/client/z-api-client';

export const LocationsStore = createContext({});

export const LocationsProvider = ({ children }) => {
  const [locations, dispatchLocations] = useLocationsReducer();
  const countryCodeToNamePromiseRef = useRef(null);

  const getCountryCodeToNameMap = useCallback(async () => {
    // Return cached result if available
    if (locations.countryCodeToNameMap) {
      return locations.countryCodeToNameMap;
    }
    
    // If a request is already in progress, return the same promise
    if (countryCodeToNamePromiseRef.current) {
      return countryCodeToNamePromiseRef.current;
    }
    
    // Create new promise for fetching
    countryCodeToNamePromiseRef.current = (async () => {
      const countries = await ZApiClient
        .getGeoNamesClient()
        .getCountries({ sorted: true, fields: ['country,isoCode'] });
      
      const map = countries.reduce((acc, country) => {
        if (country.isoCode && country.country) {
          acc[country.isoCode] = country.country;
        }
        return acc;
      }, {});
      
      dispatchLocations(setCountryCodeToNameMap(map));
      countryCodeToNamePromiseRef.current = null;
      
      return map;
    })();
    
    return countryCodeToNamePromiseRef.current;
  }, [locations.countryCodeToNameMap, dispatchLocations]);

  const value = {
    ...locations,
    setServedLocationsMap: (map) => dispatchLocations(setServedLocationsMap(map)),
    setServedLocationsMapReverse: (reverseMap) => (
      dispatchLocations(setServedLocationsMapReverse(reverseMap))),
    setServedLocationsOptions: (options) => (
      dispatchLocations(setServedLocationsOptions(options))),
    getCountryCodeToNameMap,
  };

  return (
    <LocationsStore.Provider value={value}>
      {children}
    </LocationsStore.Provider>
  );
};
